/**
 * Phase 7: the battle map. A square grid (5 ft per square), tokens for the party and the combat tracker's monsters,
 * and fog of war (revealed rectangles). Coordinates: columns are letters (A, B, ... Z, AA, AB ...), rows are numbers
 * from 1, so "C4" is column 3, row 4. Internally x/y are 0-based.
 */
export const MAP_FEET_PER_SQUARE = 5;
export const MAP_MIN_SIZE = 5;
export const MAP_MAX_SIZE = 60;
/** 0 -> "A", 25 -> "Z", 26 -> "AA". */
export function colLabel(x) {
    let n = x + 1;
    let s = "";
    while (n > 0) {
        const r = (n - 1) % 26;
        s = String.fromCharCode(65 + r) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}
export function squareLabel(x, y) {
    return `${colLabel(x)}${y + 1}`;
}
/** "c4" / "C4" / "AA10" -> {x, y} (0-based), or null. */
export function parseSquare(s) {
    const m = /^\s*([a-z]{1,2})\s*(\d{1,3})\s*$/i.exec(s ?? "");
    if (!m)
        return null;
    let x = 0;
    for (const ch of m[1].toUpperCase())
        x = x * 26 + (ch.charCodeAt(0) - 64);
    const y = Number(m[2]);
    if (y < 1)
        return null;
    return { x: x - 1, y: y - 1 };
}
/** Squares between two tokens' nearest edges (5e grid: diagonals count as 1). Adjacent = 1. */
export function squaresBetween(a, b) {
    const dx = Math.max(0, a.x - (b.x + b.size - 1), b.x - (a.x + a.size - 1));
    const dy = Math.max(0, a.y - (b.y + b.size - 1), b.y - (a.y + a.size - 1));
    return Math.max(dx, dy);
}
/** Feet between two squares (the measure tool): 5 ft per square, diagonals 5 ft. */
export function feetBetween(a, b) {
    return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y)) * MAP_FEET_PER_SQUARE;
}
export function tokenFeet(a, b) {
    return squaresBetween(a, b) * MAP_FEET_PER_SQUARE;
}
/** Is the square revealed (no fog, or inside a revealed rectangle)? */
export function isRevealed(m, x, y) {
    if (!m.fog)
        return true;
    return m.revealed.some(([rx, ry, rw, rh]) => x >= rx && x < rx + rw && y >= ry && y < ry + rh);
}
/** A token is visible to players if any of its squares is revealed and it isn't hidden. */
export function tokenVisible(m, t) {
    if (t.hidden)
        return false;
    if (t.kind === "pc")
        return true;
    for (let dx = 0; dx < t.size; dx++)
        for (let dy = 0; dy < t.size; dy++)
            if (isRevealed(m, t.x + dx, t.y + dy))
                return true;
    return false;
}
/** Keep a token on the board. */
export function clampToken(m, x, y, size = 1) {
    return {
        x: Math.max(0, Math.min(m.cols - size, Math.round(x))),
        y: Math.max(0, Math.min(m.rows - size, Math.round(y))),
    };
}
/** Squares a token covers, as "x,y" keys. */
function cells(t) {
    const out = [];
    for (let dx = 0; dx < t.size; dx++)
        for (let dy = 0; dy < t.size; dy++)
            out.push(`${t.x + dx},${t.y + dy}`);
    return out;
}
export function occupied(tokens, exceptId) {
    const s = new Set();
    for (const t of tokens)
        if (t.id !== exceptId && !t.down)
            for (const c of cells(t))
                s.add(c);
    return s;
}
/**
 * Move a token toward a target, up to `maxSquares` squares, one square at a time (diagonals allowed), stopping when
 * adjacent (or at `keepSquares` distance) and never stepping onto another token. Returns the end square.
 */
export function stepToward(m, mover, target, maxSquares, blocked, keepSquares = 1) {
    const r = pathMove(m, mover, (x, y) => squaresBetween({ x, y, size: mover.size }, target), keepSquares, maxSquares, blocked);
    return { x: r.x, y: r.y, squares: r.squares };
}
/**
 * Real pathfinding (breadth-first over 8 directions, 5 ft per step as in this game's diagonal rule): walls and other
 * creatures are never crossed, and a diagonal step can't squeeze between two blocked corners. The mover heads for the
 * nearest reachable square with `dist(x, y) <= goal`; when none is reachable it gets as close as it can.
 * It then walks up to `maxSquares` steps along that path.
 */
export function pathMove(m, mover, dist, goal, maxSquares, blocked) {
    const size = mover.size;
    const free = (x, y) => {
        if (x < 0 || y < 0 || x + size > m.cols || y + size > m.rows)
            return false;
        for (let dx = 0; dx < size; dx++)
            for (let dy = 0; dy < size; dy++)
                if (blocked.has(`${x + dx},${y + dy}`))
                    return false;
        return true;
    };
    const key = (x, y) => y * m.cols + x;
    const start = { x: mover.x, y: mover.y };
    const prev = new Map([[key(start.x, start.y), -1]]);
    const queue = [start];
    let best = { x: start.x, y: start.y, d: dist(start.x, start.y), steps: 0 };
    const steps = new Map([[key(start.x, start.y), 0]]);
    if (best.d > goal) {
        for (let i = 0; i < queue.length; i++) {
            const cur = queue[i];
            const cs = steps.get(key(cur.x, cur.y));
            let reached = false;
            // Orthogonal first so paths look natural.
            for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
                const nx = cur.x + dx, ny = cur.y + dy;
                if (prev.has(key(nx, ny)) || !free(nx, ny))
                    continue;
                if (dx && dy && !free(cur.x + dx, cur.y) && !free(cur.x, cur.y + dy))
                    continue; // no corner squeezing
                prev.set(key(nx, ny), key(cur.x, cur.y));
                steps.set(key(nx, ny), cs + 1);
                queue.push({ x: nx, y: ny });
                const d = dist(nx, ny);
                if (d < best.d || (d === best.d && cs + 1 < best.steps))
                    best = { x: nx, y: ny, d, steps: cs + 1 };
                if (d <= goal) {
                    reached = true;
                    break;
                }
            }
            if (reached)
                break;
        }
    }
    const path = [];
    for (let k = key(best.x, best.y); k !== -1; k = prev.get(k))
        path.unshift({ x: k % m.cols, y: Math.floor(k / m.cols) });
    const n = Math.min(maxSquares, path.length - 1);
    const end = path[n] ?? start;
    return { x: end.x, y: end.y, squares: n, path: path.slice(0, n + 1) };
}
/** The nearest free square to (x, y) for a token of `size` (spiral search). */
export function nearestFree(m, x, y, size, blocked) {
    const start = clampToken(m, x, y, size);
    for (let r = 0; r < Math.max(m.cols, m.rows); r++) {
        for (let dy = -r; dy <= r; dy++)
            for (let dx = -r; dx <= r; dx++) {
                if (Math.max(Math.abs(dx), Math.abs(dy)) !== r)
                    continue;
                const nx = start.x + dx, ny = start.y + dy;
                if (nx < 0 || ny < 0 || nx + size > m.cols || ny + size > m.rows)
                    continue;
                let ok = true;
                for (let a = 0; a < size && ok; a++)
                    for (let b = 0; b < size && ok; b++)
                        if (blocked.has(`${nx + a},${ny + b}`))
                            ok = false;
                if (ok)
                    return { x: nx, y: ny };
            }
    }
    return start;
}
/** Monster size word -> squares. */
export function sizeSquares(size) {
    const s = (size ?? "").toLowerCase();
    if (s.startsWith("garg"))
        return 4;
    if (s.startsWith("huge"))
        return 3;
    if (s.startsWith("large"))
        return 2;
    return 1;
}
/**
 * The compact block the DM sees: grid size, every token's square, and for each monster the distance to the nearest PC.
 */
export function mapSummary(m, tokens, currentCombatantId) {
    const pcs = tokens.filter((t) => t.kind === "pc" && !t.down);
    const line = (t) => {
        const where = squareLabel(t.x, t.y) + (t.size > 1 ? ` (${t.size}x${t.size})` : "");
        const flags = [t.down ? "down" : "", t.hidden ? "hidden from players" : "", t.combatantId !== null && t.combatantId === currentCombatantId ? "CURRENT TURN" : ""].filter(Boolean);
        let near = "";
        if (t.kind !== "pc" && pcs.length) {
            const n = pcs.map((p) => ({ p, d: tokenFeet(t, p) })).sort((a, b) => a.d - b.d)[0];
            near = `, ${n.d} ft from ${n.p.name}`;
        }
        return `${t.name} ${where}${near}${flags.length ? ` [${flags.join(", ")}]` : ""}`;
    };
    const order = [...tokens].sort((a, b) => (a.kind === "pc" ? 0 : 1) - (b.kind === "pc" ? 0 : 1));
    return `BATTLE MAP "${m.name}" (${m.cols}x${m.rows} squares, 5 ft each; columns A-${colLabel(m.cols - 1)}, rows 1-${m.rows}${m.fog ? "; fog of war on" : ""}). ` +
        `Positions: ${order.map(line).join("; ") || "no tokens"}. ` +
        ((m.blocked ?? []).length
            ? `Blocked (impassable) squares: ${(m.blocked ?? []).slice(0, 60).map(([x, y]) => squareLabel(x, y)).join(" ")}. ` : "") +
        "Use move_token to move monsters/NPCs on their turns (toward a target, within their speed) and place_token for creatures that appear; " +
        "players move their own tokens. Describe positions in words (\"thirty feet away, behind the cart\"), don't read out grid squares, and don't re-narrate moves you've already described.";
}
/** "C3", "C3-C8" or "C3-F5" (a rectangle) -> squares on the board, at most `max`. */
export function parseSquares(list, cols, rows, max = 300) {
    const out = new Map();
    for (const item of Array.isArray(list) ? list : []) {
        if (typeof item !== "string")
            continue;
        const [a, b] = item.split(/\s*(?:-|to|:)\s*/i);
        const p = parseSquare(a ?? ""), q = b ? parseSquare(b) : p;
        if (!p || !q)
            continue;
        for (let x = Math.min(p.x, q.x); x <= Math.max(p.x, q.x); x++)
            for (let y = Math.min(p.y, q.y); y <= Math.max(p.y, q.y); y++) {
                if (x < cols && y < rows && out.size < max)
                    out.set(`${x},${y}`, [x, y]);
            }
    }
    return [...out.values()];
}
/** Squares along an entrance edge for `n` party tokens, spread around the middle of that edge. */
export function entranceSquares(m, edge, n) {
    const out = [];
    const along = edge === "west" || edge === "east" ? m.rows : m.cols;
    const mid = Math.floor(along / 2);
    for (let i = 0; i < n; i++) {
        const off = (i % 2 ? 1 : -1) * Math.ceil(i / 2);
        const lane = Math.floor(i / Math.max(1, along)); // second row inward if the edge is full
        const a = Math.max(0, Math.min(along - 1, mid + off));
        const depth = 1 + lane;
        out.push(edge === "west" ? { x: depth, y: a } : edge === "east" ? { x: m.cols - 1 - depth, y: a } : edge === "north" ? { x: a, y: depth } : { x: a, y: m.rows - 1 - depth });
    }
    return out;
}
export const OPPOSITE_EDGE = { north: "south", south: "north", east: "west", west: "east" };
//# sourceMappingURL=map.js.map